import React, { useEffect } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
    Box,
    Button,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    Stack,
    Switch,
    Tooltip,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { ShowErrors } from 'common/components';
import CardContainer from 'common/components/CardContainer';
import {
    ReactHookFormInput,
    ReactHookFormIntlNumberInput,
} from 'common/components/form/RHF';
import productDatasheet from 'common/modules/RHFproductDatasheet';
import * as productDatasheetSelectors from 'common/modules/RHFproductDatasheet/selectors';
import productThumbnail from 'common/modules/RHFproductThumbnail';
import * as productThumbnailSelectors from 'common/modules/RHFproductThumbnail/selectors';
import yupResolver from 'common/utils/yupResolver';

import PanelPlaceholder from 'resources/placeholders/Panel.svg';

import * as actions from '../actions';
import * as selectors from '../selectors';
import validate from '../validate';

const ProductForm = ({
    changeInput,
    errors,
    filterData,
    handleClickBulkItems,
    handleEditionChange,
    handleOnSave,
    initialValues,
    isEnabledEdition,
    isFetchingDetail,
    isLoadingProductDatasheet,
    isLoadingProductThumbnail,
    isNewRecord,
    isSaving,
    isUploadingDatasheet,
    isUploadingThumbnail,
    prepareReturnCatalog,
}) => {
    const { t } = useTranslation();

    const { control, getValues, handleSubmit, reset, setValue } = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validate),
    });

    const formValues = getValues();

    useEffect(() => reset(initialValues), [initialValues]);

    const handleEditionSwitchChange = (event) => {
        handleEditionChange(event.target.checked);
    };

    const handleClickSave = (values) => handleOnSave(filterData, values);

    const isArchived = formValues?.status_flag === 'archived';

    const isLoading =
        isFetchingDetail ||
        isLoadingProductDatasheet ||
        isLoadingProductThumbnail ||
        isUploadingDatasheet ||
        isUploadingThumbnail ||
        isSaving;

    return (
        <Box sx={{ py: 2, overflowY: 'scroll' }}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Tooltip title={t('Back')}>
                    <IconButton
                        aria-label="back-button"
                        onClick={() => prepareReturnCatalog(filterData)}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                </Tooltip>

                <FormGroup>
                    <FormControlLabel
                        disabled={isLoading || isNewRecord}
                        control={
                            <Switch
                                checked={isEnabledEdition}
                                onChange={handleEditionSwitchChange}
                            />
                        }
                        label={t('Edit')}
                        labelPlacement="start"
                    />
                </FormGroup>
            </Stack>

            <productThumbnail.Container
                control={control}
                formValues={formValues}
                isEnabledEdition={isEnabledEdition}
                isFetchingDetail={isFetchingDetail}
                placeholder={PanelPlaceholder}
                setValue={setValue}
            />

            <productDatasheet.Container
                control={control}
                isEnabledEdition={isEnabledEdition}
                isFetchingDetail={isFetchingDetail}
                productId={formValues.id}
                setValue={setValue}
            />

            <form>
                <CardContainer
                    title={t('General information')}
                    isCollapsable
                    isOpen
                    loading={isFetchingDetail}
                    withDivider
                >
                    <Grid container pt={2}>
                        <Grid size={18}>
                            <ReactHookFormInput
                                control={control}
                                name="name"
                                label={t('Name')}
                                placeholder={t('Name')}
                                required
                                disabled={!isEnabledEdition || isLoading}
                            />
                        </Grid>
                    </Grid>

                    <Grid container pt={2}>
                        <Grid size={{ md: 9, xs: 18 }}>
                            <ReactHookFormInput
                                control={control}
                                name="code"
                                label={t('Product code')}
                                placeholder={t('Product code')}
                                required
                                disabled={!isEnabledEdition || isLoading}
                            />
                        </Grid>

                        <Grid size={{ md: 9, xs: 18 }}>
                            <ReactHookFormIntlNumberInput
                                append="USD"
                                control={control}
                                disabled={!isEnabledEdition || isLoading}
                                fullWidth
                                label={t('Cost')}
                                name="cost"
                                placeholder={t('Cost')}
                                prepend="$"
                                required
                            />
                        </Grid>
                    </Grid>

                    <Grid container pt={2}>
                        <Grid size={{ md: 9, xs: 18 }}>
                            <ReactHookFormIntlNumberInput
                                append="g"
                                control={control}
                                disabled={!isEnabledEdition || isLoading}
                                fullWidth
                                label={t('Weight')}
                                name="weight"
                                placeholder={t('Weight')}
                                required
                            />
                        </Grid>
                    </Grid>
                </CardContainer>

                <ShowErrors
                    errors={errors}
                    mt={2}
                    severity="error"
                    variant="filled"
                />

                {isEnabledEdition && (
                    <Grid container mt={2}>
                        <Grid
                            size="grow"
                            sx={{
                                display: 'flex',
                                flexDirection: {
                                    md: 'row',
                                    xs: 'column',
                                },
                                gap: 2,
                                justifyContent: {
                                    md: 'right',
                                    xs: 'center',
                                },
                            }}
                        >
                            {!isNewRecord && (
                                <>
                                    <Button
                                        color="secondary"
                                        disabled={isLoading}
                                        fullWidth={false}
                                        onClick={() =>
                                            handleClickBulkItems(
                                                [formValues?.id],
                                                'archived',
                                                filterData,
                                                () =>
                                                    changeInput(
                                                        'status_flag',
                                                        'archived',
                                                    ),
                                            )
                                        }
                                        sx={{
                                            width: {
                                                xs: '100%',
                                                md: 'auto',
                                            },
                                            order: { xs: 2, md: 1 },
                                            mr: { xs: 0, md: 2 },
                                        }}
                                        type="button"
                                        variant="text"
                                        visible={!isArchived}
                                    >
                                        {t('Archive')}
                                    </Button>

                                    <Button
                                        color="secondary"
                                        disabled={isLoading}
                                        fullWidth={false}
                                        onClick={() =>
                                            handleClickBulkItems(
                                                [formValues?.id],
                                                'active',
                                                filterData,
                                                () =>
                                                    changeInput(
                                                        'status_flag',
                                                        'active',
                                                    ),
                                            )
                                        }
                                        sx={{
                                            width: {
                                                xs: '100%',
                                                md: 'auto',
                                            },
                                            order: { xs: 2, md: 1 },
                                            mr: { xs: 0, md: 2 },
                                        }}
                                        type="button"
                                        variant="text"
                                        visible={isArchived}
                                    >
                                        {t('Restore')}
                                    </Button>

                                    <Button
                                        color="error"
                                        disabled={isLoading}
                                        fullWidth={false}
                                        onClick={() =>
                                            handleClickBulkItems(
                                                [formValues?.id],
                                                'deleted',
                                                filterData,
                                                () => prepareReturnCatalog(),
                                            )
                                        }
                                        sx={{
                                            width: {
                                                xs: '100%',
                                                md: 'auto',
                                            },
                                            order: { xs: 2, md: 1 },
                                            mr: { xs: 0, md: 2 },
                                        }}
                                        type="button"
                                        variant="text"
                                        visible={isArchived}
                                    >
                                        {t('Delete')}
                                    </Button>
                                </>
                            )}

                            <Button
                                disabled={isLoading}
                                fullWidth={false}
                                onClick={handleSubmit(handleClickSave)}
                                sx={{
                                    width: { xs: '100%', md: 'auto' },
                                    order: { xs: 1, md: 2 },
                                    mb: { xs: 2, md: 0 },
                                }}
                                type="button"
                                variant="outlined"
                                visible={!isArchived}
                            >
                                {isSaving
                                    ? t('Saving').concat('...')
                                    : t('Save')}
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </form>
        </Box>
    );
};

ProductForm.propTypes = {
    changeInput: PropTypes.func,
    errors: PropTypes.array,
    filterData: PropTypes.object,
    handleClickBulkItems: PropTypes.func,
    handleEditionChange: PropTypes.func,
    handleOnSave: PropTypes.func,
    initialValues: PropTypes.object,
    isEnabledEdition: PropTypes.bool,
    isFetchingDetail: PropTypes.bool,
    isLoadingProductDatasheet: PropTypes.bool,
    isLoadingProductThumbnail: PropTypes.bool,
    isNewRecord: PropTypes.bool,
    isSaving: PropTypes.bool,
    isUploadingDatasheet: PropTypes.bool,
    isUploadingThumbnail: PropTypes.bool,
    prepareReturnCatalog: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
    errors: selectors.getErrorsSave,
    initialValues: selectors.getInitialValues,
    isEnabledEdition: selectors.getEnabledEdition,
    isFetchingDetail: selectors.getIsFetchingItemDetail,
    isLoadingProductDatasheet:
        productDatasheetSelectors.getIsLoadingProductDatasheet,
    isLoadingProductThumbnail:
        productThumbnailSelectors.getIsLoadingProductThumbnail,
    isNewRecord: selectors.getIsNewRecord,
    isSaving: selectors.getIsSaving,
    isUploadingDatasheet: productDatasheetSelectors.getIsUploadingDatasheet,
    isUploadingThumbnail: productThumbnailSelectors.getIsUploadingThumbnail,
});

const mapDispatchToProps = (dispatch) => ({
    handleEditionChange: (value) => dispatch(actions.setEnabledEdition(value)),
    handleOnSave: (filterData, values) =>
        dispatch(actions.save(filterData, values)),
    prepareReturnCatalog: (filterData) =>
        dispatch(actions.returnCatalog(filterData)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductForm);
